export type QuestionType = "single-choice" | "multiple-choice" | "drag-to-match" | "drag-to-categorize" | "drag-to-order";

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  "single-choice": "Single Choice",
  "multiple-choice": "Multiple Choice",
  "drag-to-match": "Drag to Match",
  "drag-to-categorize": "Drag to Categorize",
  "drag-to-order": "Drag to Order",
};

export const QUESTION_TYPES: QuestionType[] = [
  "single-choice",
  "multiple-choice",
  "drag-to-match",
  "drag-to-categorize",
  "drag-to-order",
];

export const K_LEVEL_LABELS: Record<string, string> = {
  K1: "Remember",
  K2: "Understand",
  K3: "Apply",
  K4: "Analyze",
  K5: "Evaluate",
  K6: "Create",
  H0: "Demo",
  H1: "Guided Exercise",
  H2: "Exercise",
};

/**
 * Separator used in the admin UI to split items inside a drag-to-categorize
 * category. We use a pipe because real item text frequently contains commas
 * (the legacy separator), which would otherwise be split incorrectly.
 *
 * For backward compatibility, `splitCategorizeItems` falls back to splitting
 * on commas when the input string does not contain any pipe — so existing
 * questions stored before this change keep working until an admin re-saves
 * them with the new separator.
 */
export const CATEGORIZE_ITEM_SEPARATOR = "|";

export function splitCategorizeItems(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  if (typeof raw !== "string") return [];
  const sep = raw.includes(CATEGORIZE_ITEM_SEPARATOR) ? CATEGORIZE_ITEM_SEPARATOR : ",";
  return raw.split(sep).map((s) => s.trim()).filter(Boolean);
}

export function scoreQuestion(
  questionType: QuestionType | undefined,
  answer: string | undefined,
  question: any,
): number {
  if (!answer) return 0;
  const type = questionType || "single-choice";

  switch (type) {
    case "single-choice":
      return answer === question.correctAnswer ? 1 : 0;

    case "multiple-choice": {
      const selected = answer.split(",").filter(Boolean).sort();
      const correct = [...(question.correctAnswers || [])].sort();
      if (selected.length === 0 || correct.length === 0) return 0;
      const correctCount = selected.filter((s: string) => correct.includes(s)).length;
      const wrongCount = selected.filter((s: string) => !correct.includes(s)).length;
      if (correctCount === correct.length && wrongCount === 0) return 1;
      return Math.max(0, (correctCount - wrongCount) / correct.length);
    }

    case "drag-to-match": {
      try {
        const parsed = JSON.parse(answer) as Record<string, string>;
        const pairs = question.matchPairs || [];
        if (pairs.length === 0) return 0;
        let correct = 0;
        pairs.forEach((pair: any) => {
          if (parsed[pair.left] === pair.right) correct++;
        });
        return correct / pairs.length;
      } catch {
        return 0;
      }
    }

    case "drag-to-categorize": {
      try {
        const parsed = JSON.parse(answer) as Record<string, string>;
        const categories = question.categories || [];
        let correct = 0;
        let total = 0;
        categories.forEach((cat: any) => {
          cat.items.forEach((item: string) => {
            total++;
            if (parsed[item] === cat.name) correct++;
          });
        });
        return total > 0 ? correct / total : 0;
      } catch {
        return 0;
      }
    }

    case "drag-to-order": {
      try {
        const parsed = JSON.parse(answer) as string[];
        const correctOrder = question.orderItems || [];
        if (correctOrder.length === 0) return 0;
        let correct = 0;
        correctOrder.forEach((id: string, idx: number) => {
          if (parsed[idx] === id) correct++;
        });
        return correct / correctOrder.length;
      } catch {
        return 0;
      }
    }

    default:
      return 0;
  }
}
